import { json, type LoaderFunctionArgs } from '@remix-run/cloudflare';
import { useLoaderData } from '@remix-run/react';
import type { MetaDescriptor, MetaFunction } from '@remix-run/react';
import { drizzle } from 'drizzle-orm/d1';
import { Card } from '~/components/Card';
import { SimpleLayout } from '~/components/SimpleLayout';
import { type ArticleWithSlug } from '~/lib/articles';
import { formatDate } from '~/lib/formatDate';
import * as schema from '~/db/schema.server';

import { ArticleCard as NewArticle } from '~/components/ArticleCard';
import { SITE_META } from '~/defaults/site-meta';
import { generateTwitterTags } from '~/lib/twitter-card';
import { generateOgTags } from '~/lib/open-graph';
import { transformArticle } from '~/lib/article-mapper';

export const meta: MetaFunction = (): MetaDescriptor[] => {
  return [
    { title: 'Articles - unknownkind' },
    {
      name: 'description',
      content:
        'Articles about programming, leadership, product design, and more.',
    },
    ...generateTwitterTags(),
    ...generateOgTags(SITE_META),
  ];
};

export async function loader({ context }: LoaderFunctionArgs) {
  const db = drizzle(context.env.DB, { schema });

  const articles = await db.query.PageEntity.findMany({
    with: {
      authors: {
        with: {
          author: true,
        },
      },
      media: {
        with: {
          media: true,
        },
      },
    },
    orderBy: schema.PageEntity.date,
  });

  return json({ articles });
}

export function Article({ article }: { article: ArticleWithSlug }) {
  const date = formatDate(article.date || '');
  return (
    <article className="md:grid md:grid-cols-4 md:items-baseline">
      <Card className="md:col-span-3">
        <Card.Title href={`/articles/${article.slug}`}>
          {article.title}
        </Card.Title>
        <Card.Eyebrow as="time" dateTime={date} className="md:hidden" decorate>
          {/* formatted date */}
          {date}
        </Card.Eyebrow>
        <Card.Description>{article.description}</Card.Description>
        <Card.Cta>Read article</Card.Cta>
      </Card>
      <Card.Eyebrow as="time" dateTime={date} className="mt-1 hidden md:block">
        {/* formatted date */}
        {date}
      </Card.Eyebrow>
    </article>
  );
}

// const colors = {
//   article: 'bg-indigo-100 text-indigo-600',
//   video: 'bg-pink-100 text-pink-800',
//   'case-study': 'bg-green-100 text-green-800',
// };

export default function ArticlesIndex() {
  const { articles } = useLoaderData<typeof loader>();
  const transformedArticles = articles.map(transformArticle);
  return (
    <SimpleLayout
      title="Writing about software, company building, and the tech industry."
      intro="All of my long-form thoughts on programming, leadership, product design, and more."
    >
      {/* <div className="md:border-l md:border-zinc-100 md:pl-6 md:dark:border-zinc-700/40"> */}
      {/* <div className="flex max-w-3xl flex-col space-y-16"> */}
      <div className="md:pl-6">
        <div className="flex flex-col space-y-16">
          {transformedArticles.map(article => (
            // <Article key={article.id} article={article as ArticleWithSlug} />
            <NewArticle key={article.id} article={article as ArticleWithSlug}>
              <div className="mt-3 flex flex-row items-center justify-between space-x-1 text-xs text-zinc-500">
                {/* Article - Eyebrow */}
                <div>
                  <time dateTime={String(article.date || '')}>
                    {formatDate(article.date || '')}
                  </time>
                  {article.meta?.readingTime && (
                    <>
                      <span aria-hidden="true">&middot;</span>
                      <span>{article.meta?.readingTime} read</span>
                    </>
                  )}
                </div>
                <Card.Cta as="span" className="">
                  Read article
                </Card.Cta>
              </div>
            </NewArticle>
          ))}
        </div>
      </div>
    </SimpleLayout>
  );
}
